import "./accordion.scss";

import { useState, useEffect } from "react";
import { Collapse, Image } from "react-bootstrap";
import { LeftIcon } from "src/assets";
import { typedMemo, AccordionProps } from "src/types";

import { concatClasses } from "src/utils";

export const Accordion = typedMemo(
  ({
    title,
    head,
    children,
    icon = LeftIcon,
    flavor = "base",
    defaultExpanded = false,
    expanded: externalExpanded,
    onChange,
    classes = "d-flex justify-content-between",
    classesHead = "m-0 p-2 d-flex justify-content-between flex-nowrap",
    classesTitle = "m-0 p-0",
    classesIcon = "",
    arrowVersus = "bottom",
    iconContainerClass = "",
    classesIconExpanded,
    classesIconNotExpanded,
    classesTitleExpanded,
    classesTitleNotExpanded,
    onToggle,
  }: AccordionProps) => {
    // Usa stato interno solo se non controllato dall'esterno
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

    // Determina se è controllato dall'esterno
    const isControlled = externalExpanded !== undefined;
    const expanded = isControlled ? externalExpanded : internalExpanded;

    // Sincronizza stato interno se passa da non-controllato a controllato
    useEffect(() => {
      if (isControlled && externalExpanded !== internalExpanded) {
        setInternalExpanded(externalExpanded);
      }
    }, [externalExpanded, isControlled]);

    const handleToggle = () => {
      const newState = !expanded;

      // Se non controllato, aggiorna stato interno
      if (!isControlled) {
        setInternalExpanded(newState);
      }

      // Chiama callback esterni
      onChange?.(newState);
      onToggle?.(newState);
    };

    const Head = (
      <div className={classesHead} role="button" onClick={handleToggle}>
        {head || (
          <div
            className={concatClasses(
              classesTitle,
              expanded ? classesTitleExpanded : classesTitleNotExpanded
            )}
          >
            {title}
          </div>
        )}
        <div
          className={concatClasses(iconContainerClass, "align-items-center")}
        >
          <Image
            src={icon}
            width={16}
            height={16}
            className={concatClasses(
              `icon-v${arrowVersus}`,
              classesIcon,
              expanded ? classesIconExpanded : classesIconNotExpanded,
              expanded ? `rotate-v${arrowVersus}` : "",
              flavor === "green" ? "filter-159881" : ""
            )}
          />
        </div>
      </div>
    );

    const Separator = (
      <>
        {flavor === "green" && <hr className="separator separator-green" />}
        {flavor === "grey" && <hr className="separator separator-grey" />}
      </>
    );

    const Collapsible = (
      <Collapse in={expanded}>
        <div className="m-0 p-0">{children}</div>
      </Collapse>
    );

    return (
      <main className={classes}>
        {Head}
        {Separator}
        {Collapsible}
      </main>
    );
  }
);
